import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAppStore, useIsSessionBusy, isSessionBusy } from '@/stores/appStore';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ToolInvocationModal } from '@/components/common/ToolInvocationModal';
import { SchedulePromptModal } from './SchedulePromptModal';
import { SessionTokenBadge } from './SessionTokenBadge';
import { ContextIndicator } from './ContextIndicator';
import { HitlApprovals } from './HitlApprovals';
import { PromptHints } from './PromptHints';
import { useChatService, runChatTurn } from '@/hooks/useChatService';
import { ChatSession, MCPTool, Message } from '@/types';
import { useTranslation } from '@/i18n';
import { apiClient } from '@/utils/apiClient';
import { hasValidationErrors } from './MessageItem';
import { RefreshCw, Trash2 } from 'lucide-react';

// Maximum number of auto-retry attempts for validation errors
const MAX_AUTO_RETRY_ATTEMPTS = 3;

/**
 * STORY-299: every chat the store holds, once each. The open chat comes first
 * because its object is the freshest copy, in case it is not in the list yet.
 */
function chatsOf(state: { currentSession: ChatSession | null; sessions: ChatSession[] }): ChatSession[] {
  const { currentSession, sessions } = state;
  if (!currentSession) return sessions;
  return [currentSession, ...sessions.filter((s) => s.id !== currentSession.id)];
}

export const ChatInterface: React.FC = () => {
  const { currentSession, createSession, sessionsLoading, historyLoadingIds, addMessage, clearMessagesAndHistory, settings, llmProviders } = useAppStore();
  const sessionStats = useAppStore((state) => state.sessionStats);
  const refreshSessionStats = useAppStore((state) => state.refreshSessionStats);

  // STORY-290 (R-4): one refresh of the session's server-side usage whenever
  // the session opens or a new answer lands — whichever path sent it (input,
  // auto-retry, re-ask, execute-plan). Both token badges and the context
  // counter read what this fetches, so they change together. The backend
  // records usage and the turn before it responds, so no delay is needed.
  const currentSessionId = currentSession?.id;
  // STORY-299: busy is the OPEN chat's own turn, never another chat's.
  const isBusy = useIsSessionBusy(currentSessionId);
  const retryTurn = useAppStore((state) => (currentSessionId ? state.pendingBySession[currentSessionId]?.retry : undefined));
const lastMessage = currentSession?.messages[currentSession.messages.length - 1];
  const lastAnswerId = lastMessage?.type === 'assistant' ? lastMessage.id : undefined;
  useEffect(() => {
    if (!currentSessionId) return;
    void refreshSessionStats(currentSessionId);
  }, [currentSessionId, lastAnswerId, refreshSessionStats]);
  // Stable refs for values used in callbacks — prevents callback recreation on every store update
  const currentSessionRef = useRef(currentSession);
  currentSessionRef.current = currentSession;
const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const [selectedTool, setSelectedTool] = useState<{
    tool: MCPTool;
    serverName: string;
    serverId: string;
  } | null>(null);
  const [scheduleModal, setScheduleModal] = useState<{
    open: boolean;
    promptText: string;
    resultPreview: string;
  }>({ open: false, promptText: '', resultPreview: '' });
  const { t, language } = useTranslation();
  const { sendMessage } = useChatService();

  // STORY-299: the auto-retry's progress lives on its chat's turn
  // (pendingBySession[id].retry), not in this component, so it stays with the
  // chat that is retrying when the user switches to another.

  // Track which messages have been processed for auto-retry to avoid loops.
  // Message ids are unique across chats, so one set serves all of them.
const processedMessagesRef = useRef<Set<string>>(new Set());

  // Scrolling is handled by MessageList component

  /**
   * Find the previous user message for a given message index.
   */
  const findPreviousUserQuestion = useCallback((messages: Message[], fromIndex: number): string | null => {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (messages[i].type === 'user') {
        return messages[i].content;
      }
    }
    return null;
  }, []);

  /**
   * Auto-retry handler for validation errors.
   * Automatically retries the original question with corrected instructions.
   * STORY-299: `sessionId` is the chat whose answer failed validation; the
   * retry is posted for it and written to it even if another chat is open.
   */
  const handleAutoRetry = useCallback(async (
    sessionId: string,
    originalQuestion: string,
    _failedMessageId: string, // unused since STORY-299: the retry indicator reads the chat's turn
    attemptNumber: number
  ) => {
    if (isSessionBusy(sessionId)) return;

    // Create a retry message that instructs the LLM to fix the validation error
    const lang = settingsRef.current.language || 'ru';
    const retryMessage = lang === 'ru'
      ? `🔄 Автоматическая повторная попытка (${attemptNumber}/${MAX_AUTO_RETRY_ATTEMPTS})

Предыдущий вызов завершился ошибкой валидации. Исправьте параметры и попробуйте снова.

Оригинальный вопрос: "${originalQuestion}"

⚠️ ВАЖНО: Следуйте инструкциям в сообщении об ошибке выше. НЕ используйте взаимоисключающие параметры вместе. Вызовите инструмент с ИСПРАВЛЕННЫМИ параметрами ПРЯМО СЕЙЧАС!`
      : `🔄 Auto-retry attempt (${attemptNumber}/${MAX_AUTO_RETRY_ATTEMPTS})

Previous call failed with validation error. Fix parameters and try again.

Original question: "${originalQuestion}"

⚠️ IMPORTANT: Follow the instructions in the error message above. DO NOT use mutually exclusive parameters together. Call the tool with CORRECTED parameters RIGHT NOW!`;

    await runChatTurn({
      sessionId,
      retry: { attempt: attemptNumber },
      request: { content: retryMessage, language: lang },
      // Add retry message as a system message (not user, to avoid confusion)
      onStart: () => addMessage({
        content: retryMessage,
        type: 'user',
        metadata: { isAutoRetry: true, attempt: attemptNumber }
      }, sessionId),
      onResponse: (response) => {
        const newMessageId = `retry-${Date.now()}`;

        addMessage({
          content: response.content,
          type: 'assistant',
          metadata: {
            ...(response.tool_calls && { toolCalls: response.tool_calls }),
            ...(response.error && { error: response.error }),
            autoRetryAttempt: attemptNumber,
          },
        }, sessionId);

        // Check if this response ALSO has validation errors
        if (hasValidationErrors(response.tool_calls) && attemptNumber < MAX_AUTO_RETRY_ATTEMPTS) {
          // Schedule another retry, for the same chat
          setTimeout(() => {
            handleAutoRetry(sessionId, originalQuestion, newMessageId, attemptNumber + 1);
          }, 1000);
        }
      },
      onError: (error) => {
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
        addMessage({
          content: lang === 'ru'
            ? `Ошибка при автоматической повторной попытке: ${errorMessage}`
            : `Error during auto-retry: ${errorMessage}`,
          type: 'assistant',
          metadata: { error: errorMessage },
        }, sessionId);
      },
    });
  }, [addMessage]); // Stable deps only — reads settings from refs

  /**
   * Effect to detect new messages with validation errors and auto-retry.
   * STORY-299: every chat is checked, not only the open one, so an answer
   * that lands in a background chat is retried for that chat while the user
   * is elsewhere. The key changes whenever any chat's last message or turn
   * changes; a message count would miss a background answer, or a return to
   * a chat of the same length.
   * EQUIVALENT MUTANT (dropping the busy bit from the key): runChatTurn writes
   * the answer and ends the turn in the same synchronous step, so React
   * renders once with both. The bit stays so a write made while the chat is
   * still busy is checked again once its turn ends.
   */
  const retryWatchKey = useAppStore((state) => chatsOf(state)
    .map((s) => `${s.id}:${s.messages[s.messages.length - 1]?.id ?? ''}:${state.pendingBySession[s.id] ? 1 : 0}`)
    .join('|'));
  useEffect(() => {
    chatsOf(useAppStore.getState()).forEach(checkChat);
  function checkChat(session: ChatSession) {
    if (session.messages.length === 0 || isSessionBusy(session.id)) return;

    const messages = session.messages;
    const lastMessage = messages[messages.length - 1];

    // Only process assistant messages
    if (lastMessage.type !== 'assistant') return;

    // Skip if already processed
    if (processedMessagesRef.current.has(lastMessage.id)) return;

    // Skip if this is already a retry response
    if (lastMessage.metadata?.autoRetryAttempt) return;

    // Check for validation errors
    const toolCalls = lastMessage.metadata?.toolCalls;
    if (!hasValidationErrors(toolCalls)) return;

    // Mark as processed to avoid re-processing
    processedMessagesRef.current.add(lastMessage.id);

    // Find the original user question
    const lastMessageIndex = messages.length - 1;
    const originalQuestion = findPreviousUserQuestion(messages, lastMessageIndex);

    if (!originalQuestion) return;

    // Skip if the "user" message was itself an auto-retry
    const previousMessage = messages[lastMessageIndex - 1];
    if (previousMessage?.metadata?.isAutoRetry) {
      const attemptNumber = (previousMessage.metadata.attempt as number) || 1;
      if (attemptNumber >= MAX_AUTO_RETRY_ATTEMPTS) {
        return; // Max retries reached
      }
    }

    // Start auto-retry
    // STORY-299: the chat is fixed now; the user may switch before the timer fires.
    console.log('🔄 Validation error detected, starting auto-retry...');
    const sessionId = session.id;
    setTimeout(() => {
      handleAutoRetry(sessionId, originalQuestion, lastMessage.id, 1);
    }, 1500);
  }
  }, [retryWatchKey, handleAutoRetry, findPreviousUserQuestion]);

  /**
   * Handler for "Clear & Retry" button — clears conversation history
   * (both local and server-side) then re-sends the original question.
   */
  const handleClearAndRetry = useCallback(async (originalQuestion: string) => {
    const session = currentSessionRef.current;
    if (!session) return;
    // STORY-299: the chat is fixed before any await.
    const sessionId = session.id;
    const lang = settingsRef.current.language || 'ru';
    const s = settingsRef.current;
    const activeLLM = s.llmMode === 'single' && s.selectedLLMProvider
      ? llmProviders.find(p => p.id === s.selectedLLMProvider)
      : llmProviders.find(p => p.enabled);
    const model_preference = s.llmMode === 'single' && activeLLM ? `${activeLLM.type}-${activeLLM.model}` : 'auto';

    await runChatTurn({
      sessionId,
      request: { content: originalQuestion, language: lang, model_preference },
      onStart: async () => {
        // 1. Clear all messages + server-side history.
        // EQUIVALENT MUTANT (no id): onStart runs synchronously from the
        // click, while the open chat is still this one, so the default target
        // is the same chat. The id is passed so that does not have to hold.
        await clearMessagesAndHistory(sessionId);
        // 2. Re-send the original question
        addMessage({ content: originalQuestion, type: 'user' }, sessionId);
      },
      onResponse: (response) => {
        addMessage({
          content: response.content,
          type: 'assistant',
          metadata: {
            ...(response.tool_calls && { toolCalls: response.tool_calls }),
            ...(response.error && { error: response.error }),
            ...(response.token_usage && { tokens: response.token_usage.total_tokens }),
            ...(response.pagination && { pagination: response.pagination }),
            ...(response.table_data && { tableData: response.table_data }),
            // STORY-298: parity with useChatService (useChatService.chart.test.tsx).
            // Like the proposal line below, deleting THIS line survives the suite
            // (mutant F12): the clear-and-retry path has no render test.
            ...(response.chart_data?.length && { chartData: response.chart_data }),
            // STORY-275: parity with useChatService. Only the main send path is
            // tested (useChatService.proposal.test.tsx); deleting THIS line survives
            // the suite (mutant F19) — the clear-and-retry path has no render test.
            ...(response.alert_rule_proposal && { alertRuleProposal: response.alert_rule_proposal }),
            // STORY-304: parity with useChatService.
            ...(response.incident_card && { incidentCard: response.incident_card }),
          },
        }, sessionId);
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : String(err);
        addMessage({ content: msg, type: 'assistant', metadata: { error: msg } }, sessionId);
      },
    });
  }, [clearMessagesAndHistory, addMessage, llmProviders]);

  /**
   * Handler for "Execute Plan" button - sends a forceful request to the LLM
   * that includes the original question and demands actual execution.
   */
  const handleExecutePlan = useCallback(async (planContent: string, originalQuestion: string) => {
    const session = currentSessionRef.current;
    if (!session) return;
    // STORY-299: the chat is fixed before any await.
    const sessionId = session.id;

    const lang = settingsRef.current.language || 'ru';
    // Create a forceful follow-up message
    const forceExecuteMessage = lang === 'ru'
      ? `⚠️ ВЫПОЛНИТЕ НЕМЕДЛЕННО! Вы предложили план, но не выполнили его.

Оригинальный вопрос: "${originalQuestion}"

Ваш план: ${planContent.slice(0, 500)}...

ВЫЗОВИТЕ ИНСТРУМЕНТЫ ПРЯМО СЕЙЧАС! НЕ объясняйте снова - СДЕЛАЙТЕ ЭТО!`
      : `⚠️ EXECUTE IMMEDIATELY! You suggested a plan but didn't execute it.

Original question: "${originalQuestion}"

Your plan: ${planContent.slice(0, 500)}...

CALL THE TOOLS RIGHT NOW! DO NOT explain again - DO IT!`;

    await runChatTurn({
      sessionId,
      request: { content: forceExecuteMessage, language: lang },
      // Add the force-execute message as a user message
      onStart: () => addMessage({ content: forceExecuteMessage, type: 'user' }, sessionId),
      onResponse: (response) => {
        addMessage({
          content: response.content,
          type: 'assistant',
          metadata: {
            ...(response.tool_calls && { toolCalls: response.tool_calls }),
            ...(response.error && { error: response.error }),
          },
        }, sessionId);
      },
      onError: (error) => {
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
        addMessage({
          content: lang === 'ru'
            ? 'Произошла ошибка при выполнении плана.'
            : 'An error occurred while executing the plan.',
          type: 'assistant',
          metadata: { error: errorMessage },
        }, sessionId);
      },
    });
  }, [addMessage]); // Stable deps only — reads currentSession/settings from refs

  /**
   * Handler for "Schedule" button on assistant messages.
   * Opens a modal pre-filled with the user prompt and result preview.
   */
  const handleSchedulePrompt = useCallback((promptText: string, resultPreview: string) => {
    setScheduleModal({ open: true, promptText, resultPreview });
  }, []);

  // Do not auto-create a session; require explicit user action

  if (!currentSession) {
    // Show spinner while sessions are loading from server
    if (sessionsLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <div className="h-5 w-5 border-2 border-gray-300 dark:border-gray-500 border-t-blue-500 rounded-full animate-spin" />
            {language === 'ru' ? 'Загрузка чатов…' : 'Loading chats…'}
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {language === 'ru' ? 'Нет активного чата' : 'No active chat'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {language === 'ru' ? 'Нажмите "+" в правом верхнем углу или используйте кнопку ниже, чтобы начать разговор.' : 'Click "+" in the top right corner or use the button below to start a conversation.'}
          </p>
          <button
            onClick={() => createSession()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            {language === 'ru' ? 'Начать новый чат' : 'Start New Chat'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 relative">
      {/* Context indicator + token badge strip */}
      <div className="flex-shrink-0">
        <div className="flex items-center gap-2 px-4">
          <ContextIndicator
            sessionId={currentSession.id}
            className="flex-1"
          />
          {currentSession.messages.length > 0 && (
            <button
              onClick={async () => {
                try {
                  await apiClient.chat({
                    content: 'clear context',
                    session_id: currentSession.id,
                    language: settings.language || 'ru',
                  });
                } catch {}
                void refreshSessionStats(currentSession.id);
              }}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
              title={settings.language === 'ru' ? 'Очистить контекст' : 'Clear context'}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {((sessionStats[currentSession.id]?.total_tokens ?? 0) > 0
          || (currentSession.tokenUsage?.totalTokens ?? 0) > 0) && (
          <div className="flex justify-end px-4 py-1">
            <SessionTokenBadge
              tokenUsage={currentSession.tokenUsage}
              sessionId={currentSession.id}
            />
          </div>
        )}
      </div>

      {/* STORY-165 — writes awaiting human approval (hidden when none) */}
      <div className="flex-shrink-0">
        <HitlApprovals refreshKey={currentSession.messages.length} />
      </div>

      {/* Messages area */}
      <div className="flex-1 min-h-0 max-h-[calc(100vh-10rem)] overflow-y-auto chat-scroll px-4 py-6">
        {currentSession.messages.length === 0 && historyLoadingIds.includes(currentSession.id) ? (
          /* History still being fetched — not an empty chat yet (F-6a) */
          <div className="flex items-center justify-center h-full" role="status">
            <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <div className="h-5 w-5 border-2 border-gray-300 dark:border-gray-500 border-t-blue-500 rounded-full animate-spin" />
              {t.chat.loadingHistory}
            </div>
          </div>
        ) : currentSession.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md mx-auto">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {language === 'ru' ? 'Начните разговор' : 'Start a conversation'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {language === 'ru' ? 'Введите сообщение ниже, чтобы начать общение с AI-ассистентом. Вы можете задавать вопросы, запрашивать помощь с кодом или обсуждать любые темы.' : 'Type a message below to begin chatting with your AI assistant. You can ask questions, request code help, or discuss any topic.'}
              </p>
              {/* STORY-152 — starter-prompt hints (empty chat only) */}
              <PromptHints onSelect={sendMessage} disabled={isBusy} />
            </div>
          </div>
        ) : (
          <>
            <MessageList
              messages={currentSession.messages}
              onExecutePlan={handleExecutePlan}
              onSchedulePrompt={handleSchedulePrompt}
              onClearAndRetry={handleClearAndRetry}
            />
            {/* Typing indicator / skeleton while loading */}
            {isBusy && (
              <div className="my-4 flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400" role="status">
                {retryTurn ? (
                  // Auto-retry progress indicator
                  <>
                    <RefreshCw className="h-2 w-4 animate-spin text-amber-500" />
                    <span className="text-amber-600 dark:text-amber-400 font-small">
                      {language === 'ru' 
                        ? `Автоматическая повторная попытка (${retryTurn.attempt}/${MAX_AUTO_RETRY_ATTEMPTS})...`
                        : `Auto-retrying (${retryTurn.attempt}/${MAX_AUTO_RETRY_ATTEMPTS})...`}
                    </span>
                  </>
                ) : (
                  // Normal typing indicator with model name.
                  // BUG-1.3.5_2 (STORY-220): find(p => p.enabled) showed the
                  // TOP enabled provider regardless of selection — honor the
                  // selected model in single mode; in auto mode the answering
                  // model is unknown until the response, so claim none.
                  (() => {
                    const activeLLM = settings.llmMode === 'single' && settings.selectedLLMProvider
                      ? llmProviders.find(p => p.id === settings.selectedLLMProvider)
                      : undefined;
                    const modelName = activeLLM?.model;
                    return (
                      <>
                        <span className="inline-flex space-x-1">
                          <span className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </span>
                        <span>
                          {modelName
                            ? (language === 'ru' ? `Генерация — ${modelName}` : `Generating — ${modelName}`)
                            : (language === 'ru' ? 'Ассистент печатает...' : 'Assistant is typing…')}
                        </span>
                      </>
                    );
                  })()
                )}
              </div>
            )}
            <div />
          </>
        )}
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <MessageInput />
      </div>

      {/* Tool Invocation Modal - Rendered in chat area */}
      {selectedTool && (
        <ToolInvocationModal
          isOpen={true}
          onClose={() => setSelectedTool(null)}
          tool={selectedTool.tool}
          serverName={selectedTool.serverName}
          serverId={selectedTool.serverId}
        />
      )}

      {/* Schedule Prompt Modal */}
      <SchedulePromptModal
        open={scheduleModal.open}
        onClose={() => setScheduleModal({ open: false, promptText: '', resultPreview: '' })}
        promptText={scheduleModal.promptText}
        resultPreview={scheduleModal.resultPreview}
      />
    </div>
  );
};